import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable } from 'rxjs'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { HelpersService } from '../../helpers.service'
import { IntegrationsService } from '../integrations.service'

@Injectable({
  providedIn: 'root'
})
export class ShopifyService {
  private builderUrl = ''
  shopifyConnected = false
  projectId = this.builderService.selectedProject

  jsonOptions = {
    headers: new HttpHeaders().set('Content-Type', 'application/json')
  }

  constructor(
    private integrationsService: IntegrationsService,
    private http: HttpClient,
    private helpers: HelpersService,
    private builderService: BuilderService
  ) {
    this.builderUrl = this.helpers.getBuilderUrl()
    this.integrationsService.getIntegrations().subscribe((response) => {
      if (response) {
        this.shopifyConnected = response.shopifyConnected === true
      }
    })
  }

  /**
   * Get Shopify OAuth authorization URL
   */
  getAuthUrl(storeUrl: string, clientId?: string, clientSecret?: string): Observable<any> {
    const payload: any = {
      projectId: this.projectId,
      shopifyStoreUrl: storeUrl
    }
    if (clientId) payload.shopifyClientId = clientId
    if (clientSecret) payload.shopifyClientSecret = clientSecret
    const params = new URLSearchParams()
    params.set('data', JSON.stringify(payload))
    return this.http.get(`${this.builderUrl}shopify-auth-url?${params.toString()}`, this.jsonOptions)
  }

  /**
   * Connect to Shopify (verify credentials)
   */
  connect(data: any): Observable<any> {
    const payload = {
      ...data,
      projectId: this.projectId
    }

    return this.http.post(`${this.builderUrl}shopify-connect`, payload, this.jsonOptions)
  }

  /**
   * Sync all products from Shopify to Stacks
   */
  syncAllProducts(): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-sync-products`, {
      projectId: this.projectId
    }, this.jsonOptions)
  }

  /**
   * Sync all collections (categories) from Shopify to Stacks
   */
  syncAllCollections(): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-sync-collections`, {
      projectId: this.projectId
    }, this.jsonOptions)
  }

  /**
   * Sync all orders from Shopify to Stacks
   */
  syncAllOrders(): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-sync-orders`, {
      projectId: this.projectId
    }, this.jsonOptions)
  }

  /**
   * Push a single product to Shopify
   */
  pushProduct(productData: any): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-push-product`, {
      projectId: this.projectId,
      productData
    }, this.jsonOptions)
  }

  /**
   * Push categories to Shopify as custom collections
   */
  pushCollections(categories: any[]): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-push-collection`, {
      projectId: this.projectId,
      categories
    }, this.jsonOptions)
  }

  /**
   * Push an order to Shopify
   */
  pushOrder(orderData: any, userData: any): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-push-order`, {
      projectId: this.projectId,
      orderData,
      userData
    }, this.jsonOptions)
  }

  /**
   * Register webhooks with Shopify
   */
  registerWebhooks(): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-register-webhooks`, {
      projectId: this.projectId
    }, this.jsonOptions)
  }

  /**
   * Update order status in Shopify
   */
  updateOrderStatus(shopifyOrderId: string | number | null, status: string, stacksOrderId?: string | number): Observable<any> {
    return this.http.post(`${this.builderUrl}shopify-update-order-status`, {
      projectId: this.projectId,
      shopify_order_id: shopifyOrderId,
      stacks_order_id: stacksOrderId,
      status
    }, this.jsonOptions)
  }
}
